import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
    Quest,
    QuestCheckResult,
    QuestDisplayType,
    QuestionType,
    QuestService,
    QuestValueChange,
} from '../../../../quest/src/public_api';
import { BomClassification, BomQuantity, BomSolveParams, ConfigitAceApiService } from './configit-ace-api.service';
import { ConfigitAceQuestModelTransform } from './configit-ace-quest-model-transform';
import { TranslateService } from '@ngx-translate/core';
import { concatMap, map } from 'rxjs/operators';
import { ConfigurationService } from '../services/configuration.service';
import {
    ConfigitAceAssignedBy,
    ConfigitAceAssignment,
    ConfigitAceConfiguration,
    ConfigitAceSection,
    ConfigitAceValueType,
    ConfigitAceVariable,
} from './configit-ace.model';
import { ConflictingValueDialogComponent } from '../conflicting-value-dialog/conflicting-value-dialog.component';
import { fromIsoDate, singletonValues } from './configit-ace.helper';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
    providedIn: 'root',
})
export class ConfigitAceQuestAdapterService implements QuestService {
    transformer: ConfigitAceQuestModelTransform;

    constructor(
        private api: ConfigitAceApiService,
        private configuration: ConfigurationService,
        private dialog: MatDialog,
        translate: TranslateService
    ) {
        this.transformer = new ConfigitAceQuestModelTransform(translate);
    }

    changeDisplayType(type: QuestDisplayType) {
        this.transformer.changeDisplayType(type);
    }

    get(id: string): Observable<Quest> {
        // in configit ace, these variables always have to be set
        const assignments: ConfigitAceAssignment[] = [
            {
                variableId: 'DIM_BUILDDATE',
                instanceId: 'ROOT',
                value: new Date().toISOString(),
            },
            {
                variableId: 'SALESORG',
                instanceId: 'ROOT',
                value: this.configuration.plant,
            },
        ];
        return this.api
            .configure(id, { salesOrg: this.configuration.plant, language: this.configuration.lang, assignments })
            .pipe(map((resp) => this.transformer.transform(resp)));
    }

    check(changed: QuestValueChange): Observable<QuestCheckResult> {
        const original: ConfigitAceConfiguration = changed.quest.original;
        const value = this.prepareValue(changed);
        const newAssignments = (Array.isArray(value) ? value : [value])
            .filter(Boolean)
            .map((value) => ({ variableId: changed.id, instanceId: changed.question?.instanceId, value }));
        const assignments = this.addAssignment(
            newAssignments,
            original.configuration.configurationAssignments.filter((a) => a.assignedBy === ConfigitAceAssignedBy.user)
        );
        const params = { salesOrg: this.configuration.plant, language: this.configuration.lang, assignments };
        return this.api.configure(original.product.id, params).pipe(
            concatMap((resp) => this.checkConflicts(resp, original)),
            map((resp) => this.transformer.transform(resp)),
            map((transformed: Quest) => ({ model: transformed }))
        );
    }

    submit(model: Quest): Observable<QuestCheckResult> {
        return of({ errors: false, model });
    }

    public bomSolve(model: Quest) {
        const config = <ConfigitAceConfiguration>model.original;
        const assignments = config.configuration.configurationAssignments;
        const classifications: BomClassification[] = this.getClassificationsFromAssignments(assignments, config);
        const categoryQuantities: BomQuantity[] = this.getQuantitiesFromAssignments(
            assignments,
            'AT_SAP_CATEGORYQUANTITY_',
            config
        );
        const itemQuantities: BomQuantity[] = this.getQuantitiesFromAssignments(
            assignments,
            'AT_SAP_ITEMQUANTITY_',
            config
        );
        const params: BomSolveParams = {
            salesOrg: this.configuration.plant,
            application: this.configuration.application,
            classifications,
            quantities: { categoryQuantities, itemQuantities },
        };
        return this.api.bomSolve(params).pipe(map(({ bomItems }) => bomItems));
    }

    private getClassificationsFromAssignments(
        assignments: ConfigitAceAssignment[],
        config: ConfigitAceConfiguration
    ): BomClassification[] {
        const reduced = this.transformAssignments(assignments, 'AT_SAP_CLASSIFICATION_', config, true)
            .map(({ id, value }) => ({
                id,
                values: [value?.toString()],
            }))
            .reduce<Record<string, BomClassification>>((prev, curr) => {
                const existing = prev[curr.id];
                prev[curr.id] = existing ? { ...existing, values: [...existing.values, ...curr.values] } : curr;
                return prev;
            }, {});
        return Object.values(reduced);
    }

    private getQuantitiesFromAssignments(
        assignments: ConfigitAceAssignment[],
        attribute: string,
        config: ConfigitAceConfiguration
    ): BomQuantity[] {
        return this.transformAssignments(assignments, attribute, config, false).map(({ id, value }) => ({
            id,
            quantity: Number(value) || 0,
        }));
    }

    private transformAssignments(
        assignments: ConfigitAceAssignment[],
        attribute: string,
        config: ConfigitAceConfiguration,
        convertNumber: boolean
    ): { id: string; value: string }[] {
        return assignments
            .filter((a) => a.variableId.startsWith(attribute))
            .map((assignment) => {
                const id = assignment.variableId.replace(attribute, '');
                const variable = this.findVariableInSections(assignment.variableId, config.sections);
                const value =
                    convertNumber && variable.valueType === ConfigitAceValueType.number
                        ? this.numericFormat(Number(assignment.value))
                        : assignment.value;

                return { id, value };
            });
    }

    private numericFormat(value: number) {
        return new Intl.NumberFormat('de-DE', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(value);
    }

    private prepareValue(changed: QuestValueChange) {
        if (changed.question.type === QuestionType.date && changed.value) {
            // configit requires date in local format
            return fromIsoDate(changed.value);
        }
        return changed.value;
    }

    private addAssignment(
        newAssignments: ConfigitAceAssignment[],
        existingAssignments: ConfigitAceAssignment[]
    ): ConfigitAceAssignment[] {
        const assignmentsWithoutNew = existingAssignments.filter(
            (assignment) => !newAssignments.some((a) => a.variableId === assignment.variableId)
        );
        return [...assignmentsWithoutNew, ...newAssignments];
    }

    private checkConflicts(
        updated: ConfigitAceConfiguration,
        old: ConfigitAceConfiguration
    ): Observable<ConfigitAceConfiguration> {
        const conflictingAssignments = updated.removedAssignments.variableAssignments.filter((assignment) => {
            const variable = updated.sections
                .map((sec) => this.findVariable(assignment.variable.id, sec))
                ?.find((result) => !!result);
            const value = singletonValues(variable?.values || []).find((v) => v.value === assignment.value.value);
            return value?.incompatible;
        });
        if (conflictingAssignments.length) {
            const items = conflictingAssignments.map((ca) => `${ca.variable.name} = ${ca.value.value}`);
            const prompt = this.dialog.open(ConflictingValueDialogComponent, {
                data: items,
                disableClose: true,
            });
            return prompt.afterClosed().pipe(
                // and revert if not agreed to continue
                map((agree) => (agree && updated) || old)
            );
        }
        return of(updated);
    }

    private findVariable(id: string, section: ConfigitAceSection): ConfigitAceVariable | undefined {
        const found = section.variables.find((v) => v.id === id);
        if (found) {
            return found;
        }
        return this.findVariableInSections(id, section.sections);
    }

    private findVariableInSections(id: string, sections: ConfigitAceSection[]): ConfigitAceVariable | undefined {
        return sections.map((sub) => this.findVariable(id, sub)).find((result) => !!result);
    }
}
